import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { api, resolveImg } from "../lib/api";
import { Nav } from "../components/Nav";
import { AdModal } from "../components/AdModal";
import { ShareSheet } from "../components/ShareSheet";
import { CardPreview } from "./Customizer";
import { Check, Share2, Download, PartyPopper, LayoutDashboard } from "lucide-react";

export default function SendCard() {
  const location = useLocation();
  const navigate = useNavigate();
  const greeting = location.state?.greeting;
  const [showAd, setShowAd] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [sentDone, setSentDone] = useState(false);

  if (!greeting) {
    return (
      <div className="min-h-screen"><Nav />
        <div className="nb-card p-10 text-center max-w-md mx-auto mt-20">
          <p className="font-semibold">This greeting link expired in your session.</p>
          <Link to="/templates" className="nb-btn bg-[#FFB5A7] px-6 py-3 mt-4 inline-flex">Create a new one</Link>
        </div>
      </div>
    );
  }

  // Share a rich-preview link so messaging apps show the card image + "X sent you a greeting".
  const shareUrl = `${window.location.origin}/api/share/${greeting.share_token}`;
  const shareText = `${greeting.sender_name} sent you a greeting on SendOccasions!`;

  // Only fires when the user explicitly confirms they sent it (or completes an in-app email).
  const handleShared = () => {
    api.post(`/greetings/${greeting.id}/mark-sent`, { via: "share" }).catch(() => {});
    setShowSheet(false);
    setShowAd(true);
    setSentDone(true);
  };

  const downloadCard = async () => {
    if (!greeting.card_image_url) return;
    try {
      const resp = await fetch(resolveImg(greeting.card_image_url));
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "greeting.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <AdModal open={showAd} onClose={() => setShowAd(false)} />
      <ShareSheet
        open={showSheet}
        url={shareUrl}
        text={shareText}
        greetingId={greeting.id}
        cardImageUrl={greeting.card_image_url}
        originUrl={window.location.origin}
        onClose={() => setShowSheet(false)}
        onShared={handleShared}
      />

      <div className="max-w-5xl mx-auto px-5 py-10 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          {sentDone && !showAd ? (
            <div className="animate-pop-in" data-testid="sent-success">
              <div className="inline-flex items-center gap-2 nb-card px-3 py-1 bg-[#A8E6CF] mb-4">
                <PartyPopper className="w-4 h-4" /> <span className="text-xs font-bold uppercase tracking-widest">Greeting sent!</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-display font-bold">Your greeting is on its way 🎉</h1>
              <p className="text-[#4A4A4A] mt-2 max-w-sm">
                Nicely done! Want to brighten someone else's day too? It only takes a few seconds.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                <button onClick={() => navigate("/templates")} data-testid="send-another-btn" className="nb-btn bg-[#FFB5A7] px-7 py-3.5 text-lg">
                  <Share2 className="w-5 h-5" /> Send another
                </button>
                <button onClick={() => navigate("/dashboard")} data-testid="go-dashboard-btn" className="nb-btn bg-white px-7 py-3.5 text-lg">
                  <LayoutDashboard className="w-5 h-5" /> Go to dashboard
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 nb-card px-3 py-1 bg-[#A8E6CF] mb-4">
                <Check className="w-4 h-4" /> <span className="text-xs font-bold uppercase tracking-widest">Ready to send</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-display font-bold">Send your greeting 🎉</h1>
              <p className="text-[#4A4A4A] mt-2 max-w-sm">
                Pick how you want to send it — text, email, WhatsApp, social, or copy the link. It's always free, and it only counts as "sent" once you confirm.
              </p>

              <button onClick={() => setShowSheet(true)} data-testid="open-share-sheet-btn" className="nb-btn bg-[#FFB5A7] w-full sm:w-auto px-8 py-4 text-lg mt-6">
                <Share2 className="w-5 h-5" /> Send / Share this greeting
              </button>

              {greeting.card_image_url && (
                <div className="mt-4">
                  <button onClick={downloadCard} data-testid="download-card-btn-inline" className="nb-btn bg-white px-6 py-3 inline-flex">
                    <Download className="w-5 h-5" /> Save card as image
                  </button>
                </div>
              )}

              <p className="text-sm text-[#4A4A4A] mt-4">
                Tip: choose <b>Email (in app)</b> inside the picker to deliver it straight to someone's inbox.
              </p>
            </>
          )}
        </div>

        <div>
          <CardPreview
            template={{ image_url: greeting.template_image, title: greeting.template_title, category: greeting.category }}
            message={greeting.message} recipient={greeting.recipient_name}
            sender={greeting.sender_name} photoUrl={greeting.photo_url} bg={greeting.bg_color}
            gradient={greeting.template_gradient}
          />
        </div>
      </div>
    </div>
  );
}
